import { useEffect, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import Button from '../ui/Button'
import PriorityBadge from './PriorityBadge'
import SavedIndicator from './SavedIndicator'
import TagList from './TagList'
import { classNames } from '../../lib/classNames'
import { formatDateOnly } from '../../lib/noteFilters'

export default function NoteCard({ note, onChange, onDelete, saving, onEdit, selectMode, selected, onToggleSelect }) {
    const [local, setLocal] = useState(note)

    useEffect(() => setLocal(note), [note])

    const setField = (patch) => {
        setLocal((x) => ({ ...x, ...patch }))
        onChange(patch)
    }

    const isTask = local.kind === 'task'
    const border = isTask && local.done ? 'border-emerald-600' : local.pinned ? 'border-accent' : 'border-black dark:border-white'

    const handleClick = () => (selectMode ? onToggleSelect(local._id) : onEdit())

    const handleKeyDown = (e) => {
        if (e.target !== e.currentTarget) return
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            handleClick()
        }
    }

    return (
        <div
            role="button"
            tabIndex={0}
            className={classNames(
                'relative border-2 shadow-brutal bg-white dark:bg-zinc-950 p-4 cursor-pointer transition-shadow hover:shadow-brutal-hover',
                selectMode && selected ? 'ring-2 ring-accent' : '',
                border
            )}
            onClick={handleClick}
            onKeyDown={handleKeyDown}
        >
            {selectMode && (
                <input
                    type="checkbox"
                    checked={!!selected}
                    onChange={() => onToggleSelect(local._id)}
                    onClick={(e) => e.stopPropagation()}
                    className="absolute top-2 left-2 z-10 size-5 accent-current"
                    aria-label={`Select ${local.title || 'item'}`}
                />
            )}
            <div className={classNames('flex flex-col gap-3', selectMode ? 'pl-6' : '')}>
                <div className="flex items-start gap-2" onClick={(e) => e.stopPropagation()}>
                    {isTask && !selectMode && (
                        <input
                            type="checkbox"
                            checked={!!local.done}
                            onChange={(e) => setField({ done: e.target.checked })}
                            className="size-5 mt-1 accent-current"
                            title="Mark done"
                            aria-label="Mark task done"
                        />
                    )}
                    <div className="flex-1 min-w-0 flex items-center gap-2 flex-wrap">
                        <span className="text-xl font-serif font-semibold text-black dark:text-white">{local.title || 'Untitled'}</span>
                        {isTask && <PriorityBadge priority={local.priority} />}
                    </div>
                    {!selectMode && (
                        <Button
                            variant={local.pinned ? 'primary' : 'secondary'}
                            className="text-xs px-2 py-1"
                            onClick={() => setField({ pinned: !local.pinned })}
                            title={local.pinned ? 'Unpin' : 'Pin'}
                            aria-label={local.pinned ? 'Unpin note' : 'Pin note'}
                        >
                            {local.pinned ? 'Pinned' : 'Pin'}
                        </Button>
                    )}
                </div>

                {isTask ? (
                    <p className="min-h-[100px] max-h-40 overflow-hidden whitespace-pre-wrap border-2 border-black dark:border-white p-3 text-sm text-black dark:text-white line-clamp-5">
                        {local.notes || 'No details.'}
                    </p>
                ) : (
                    <div className="prose prose-lg dark:prose-invert w-full p-3 min-h-[160px] max-h-64 overflow-hidden border-2 border-black dark:border-white line-clamp-[10]">
                        <ReactMarkdown
                            children={local.content || 'Click to add details...'}
                            remarkPlugins={[remarkGfm]}
                            components={{
                                a: ({ children, ...props }) => (
                                    <a {...props} onClick={(e) => e.stopPropagation()} target="_blank" rel="noopener noreferrer">{children}</a>
                                ),
                                input: ({ ...props }) =>
                                    props.type === 'checkbox' ? (
                                        <input {...props} onClick={(e) => e.stopPropagation()} disabled />
                                    ) : (
                                        <input {...props} />
                                    ),
                            }}
                        />
                    </div>
                )}

                {!isTask && <TagList tags={local.tags} />}

                <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                        {isTask && local.dueAt && <span className="text-sm text-gray-500">Due {formatDateOnly(local.dueAt)}</span>}
                        {!isTask && local.archived && <span className="text-sm text-gray-500">Archived</span>}
                    </div>
                    <div className="flex items-center gap-2">
                        {saving ? <span className="text-sm text-blue-600">Saving.</span> : <SavedIndicator />}
                        {!selectMode && (
                            <Button
                                variant="destructive"
                                className="text-xs px-2 py-1"
                                onClick={(e) => { e.stopPropagation(); onDelete() }}
                                title="Delete"
                                aria-label="Delete"
                            >
                                Delete
                            </Button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    )
}
